import { VCard, NameValue, AdrValue, GeoValue, ImppValue, ClientpidmapValue, GenderValue, ValueMetadata } from "../../../types/ParsedVCardTypes";
import * as Constants from '../constants-vcard4';

/**
 * VCard 4.0 Exporter - Modern Standard Format
 *
 * This exporter generates VCard 4.0 format according to RFC 6350 standard.
 * This is the recommended format for modern clients.
 *
 * Compatibility:
 * - ✅ Modern Outlook (2016+) - Full UTF-8 support
 * - ✅ Apple iOS/macOS Contacts - Native support
 * - ✅ Google Contacts - Full support
 * - ✅ Android - Full support
 *
 * Encoding:
 * - Always uses UTF-8 (mandated by RFC 6350)
 * - No charset parameter needed (implicit)
 * - Full Unicode support for all languages and special characters
 *
 * For older Outlook versions (pre-2016), use VCard 3.0 format instead.
 */

// Type for card entry values that can be processed
interface CardEntry extends ValueMetadata<any> {
    value: any;
    metadata?: Record<string, any>;
}

export function generateVCard(vCardDataArray: VCard[]): string {
    let vCardsString = '';

    vCardDataArray.forEach((vCardData: VCard) => {
        vCardsString += generateSingleVCard(vCardData);
    });

    return vCardsString;
}

function generateSingleVCard(vCardData: VCard): string {
    let vCardString = `${Constants.BEGIN_VCARD}\n${Constants.VERSION_PROP}:${Constants.VERSION}\n`;

    for (const prop in vCardData) {
        if (vCardData.hasOwnProperty(prop) && prop !== 'version' && vCardData[prop]) {
            let item = vCardData[prop];

            if (Array.isArray(item)) {
                item.forEach((subItem) => {
                    vCardString += handleValueAndMetadata(prop, subItem);
                });
            }
            else if (typeof item.value !== 'undefined') {
                vCardString += handleValueAndMetadata(prop, item);
            }
            else {
                vCardString += `${prop.toUpperCase()}:${item}\n`;
            }
        }
    }

    vCardString += `${Constants.END_VCARD}\n`;
    return vCardString;
}

function handleValueAndMetadata(prop: string, item: CardEntry): string {
    let val;
    switch (prop) {
        case Constants.N:
            val = getValueFromName(item.value);
            break;
        case Constants.ADR:
            val = getValueFromAdr(item.value);
            break;
        case Constants.ORG:
            if (typeof item.value === 'object' && item.value !== null) {
                val = [item.value.organisation, item.value.department].join(',');
            } else if (Array.isArray(item.value)) {
                val = item.value.join(',');
            } else {
                val = item.value;
            }
            break;
        case Constants.GEO:
            val = getValueFromGeo(item.value);
            break;
        case Constants.IMPP:
            val = getValueFromImpp(item.value);
            break;
        case Constants.CLIENTPIDMAP:
            val = getValueFromClientpidmap(item.value);
            break;
        case Constants.GENDER:
            val = getValueFromGender(item.value);
            break;
        case Constants.BDAY:
        case Constants.ANNIVERSARY:
            val = item.value.toISOString();
            break;
        default:
            val = Array.isArray(item.value) ? item.value.join(',') : item.value;
    }

    let res = `${prop.toUpperCase()}`;

    for (let attr in item) {
        if(attr !== 'value') {
            const attributeValue = Array.isArray(item[attr]) ? item[attr].join(',') : item[attr];
            res += `;${attr.toUpperCase()}=${attributeValue}`;
        }
    }

    res += `:${val}\n`;
    return res;
}

function getValueFromName(value : NameValue): string {
    return value.familyName.join(',') + ';' +
        value.givenName.join(',') + ';' +
        value.additionalNames.join(',') + ';' +
        value.prefixes.join(',') + ';' +
        value.suffixes.join(',');
}

function getValueFromAdr(value : AdrValue): string {
    return value.postOfficeBox.join(',') + ';' +
        value.extendedAddress.join(',') + ';' +
        value.street.join(',') + ';' +
        value.locality.join(',') + ';' +
        value.region.join(',') + ';' +
        value.postalCode.join(',') + ';' +
        value.country.join(',');
}

function getValueFromGeo(value : GeoValue): string {
    return value.latitude + ';' + value.longitude;
}

function getValueFromImpp(value : ImppValue): string {
    return value.protocol + ':' + value.address;
}

function getValueFromClientpidmap(value : ClientpidmapValue): string {
    return value.pid + ';' + value.uri;
}

function getValueFromGender(value : GenderValue): string {
    return value.sex + ';' + value.genderIdentity;
}